var _dec, _dec2, _class, _class2, _IconTroubleLine, _IconWarningLine, _IconCheckDarkLine, _IconInfoLine;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/** @jsx jsx */
/** @jsxFrag React.Fragment */
import React, { Component } from 'react';
import { withStyle, jsx } from '@instructure/emotion';
import { warn, error } from '@instructure/console';
import { omitProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { colorToHex8, isValid, contrast as getContrast } from '@instructure/ui-color-utils';
import { TextInput } from '@instructure/ui-text-input';
import { Tooltip } from '@instructure/ui-tooltip';
import { Button, IconButton } from '@instructure/ui-buttons';
import { Popover } from '@instructure/ui-popover';
import { IconCheckDarkLine, IconWarningLine, IconTroubleLine, IconInfoLine } from '@instructure/ui-icons';
import ColorIndicator from '../ColorIndicator';
import ColorMixer from '../ColorMixer';
import ColorContrast from '../ColorContrast';
import ColorPreset from '../ColorPreset';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
const acceptedCharactersForHEX = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'A', 'b', 'B', 'c', 'C', 'd', 'D', 'e', 'E', 'f', 'F', null];

/**
---
category: components
---
**/
let ColorPicker = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_class2 = class ColorPicker extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.inputContainerRef = null;
    this.handleInputContainerRef = el => {
      this.inputContainerRef = el;
      this.setLabelHeight();
    };
    this.setLabelHeight = () => {
      if (this.inputContainerRef) {
        var _this$inputContainerR;
        this.setState({
          labelHeight: this.inputContainerRef.getBoundingClientRect().y - (((_this$inputContainerR = this.inputContainerRef.parentElement) === null || _this$inputContainerR === void 0 ? void 0 : _this$inputContainerR.getBoundingClientRect().y) || 0)
        });
      }
    };
    this.checkSettings = () => {
      if (this.props.children && this.props.colorMixerSettings) {
        warn(false, 'You should either use children, colorMixerSettings or neither, not both. In this case, the colorMixerSettings will be ignored.', '');
      }
      if (this.props.value && typeof this.props.onChange !== 'function') {
        error(false, 'You provided a `value` prop on ColorPicker, which will render a controlled component. Please provide an `onChange` handler.');
      }
    };
    this.renderPopover = () => {
      var _this$props$styles;
      return jsx(Popover, {
        renderTrigger: jsx(IconButton, {
          disabled: this.props.disabled,
          screenReaderLabel: this.props.popoverButtonScreenReaderLabel || ''
        }, jsx(ColorIndicator, {
          color: `#${this.state.hexCode}`
        })),
        isShowingContent: this.state.openColorPicker,
        onShowContent: () => {
          this.setState({
            openColorPicker: true,
            mixedColor: this.state.hexCode
          });
        },
        onHideContent: () => {
          this.setState({
            openColorPicker: false
          });
        },
        on: "click",
        screenReaderLabel: this.props.popoverScreenReaderLabel,
        shouldContainFocus: true,
        shouldReturnFocus: true,
        shouldCloseOnDocumentClick: true,
        offsetY: "10rem"
      }, jsx("div", {
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.popoverContentContainer
      }, this.isDefaultPopover ? this.renderDefaultPopoverContent() : this.renderCustomPopoverContent()));
    };
    this.renderCustomPopoverContent = () => {
      const _this$props = this.props,
        children = _this$props.children,
        onChange = _this$props.onChange;
      return jsx("div", null, typeof children === 'function' && children(`#${this.mixedColorWithStrippedAlpha}`, newColor => {
        this.setState({
          mixedColor: colorToHex8(newColor).slice(1)
        });
      }, () => {
        this.setState({
          openColorPicker: false,
          hexCode: this.mixedColorWithStrippedAlpha
        });
        onChange === null || onChange === void 0 ? void 0 : onChange(`#${this.mixedColorWithStrippedAlpha}`);
      }, () => this.setState({
        openColorPicker: false,
        mixedColor: this.state.hexCode
      })));
    };
    this.renderDefaultPopoverContent = () => {
      var _this$props$styles2, _this$props2, _this$props2$colorMix, _this$props3, _this$props3$colorMix, _this$props$styles3, _this$props4, _this$props4$colorMix, _this$props$styles4, _this$props$styles5, _this$props$colorMixe, _this$props$colorMixe2;
      return jsx(React.Fragment, null, jsx("div", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.popoverContent
      }, ((_this$props2 = this.props) === null || _this$props2 === void 0 ? void 0 : (_this$props2$colorMix = _this$props2.colorMixerSettings) === null || _this$props2$colorMix === void 0 ? void 0 : _this$props2$colorMix.colorMixer) && jsx(ColorMixer, {
        value: `#${this.state.mixedColor}`,
        onChange: newColor => this.setState({
          mixedColor: colorToHex8(newColor).slice(1)
        }),
        withAlpha: this.props.colorMixerSettings.colorMixer.withAlpha,
        rgbRedInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbRedInputScreenReaderLabel,
        rgbGreenInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbGreenInputScreenReaderLabel,
        rgbBlueInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbBlueInputScreenReaderLabel,
        rgbAlphaInputScreenReaderLabel: this.props.colorMixerSettings.colorMixer.rgbAlphaInputScreenReaderLabel,
        colorSliderNavigationExplanationScreenReaderLabel: this.props.colorMixerSettings.colorMixer.colorSliderNavigationExplanationScreenReaderLabel,
        alphaSliderNavigationExplanationScreenReaderLabel: this.props.colorMixerSettings.colorMixer.alphaSliderNavigationExplanationScreenReaderLabel,
        colorPaletteNavigationExplanationScreenReaderLabel: this.props.colorMixerSettings.colorMixer.colorPaletteNavigationExplanationScreenReaderLabel
      }), ((_this$props3 = this.props) === null || _this$props3 === void 0 ? void 0 : (_this$props3$colorMix = _this$props3.colorMixerSettings) === null || _this$props3$colorMix === void 0 ? void 0 : _this$props3$colorMix.colorPreset) && jsx("div", {
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.popoverContentBlock
      }, jsx(ColorPreset, {
        label: this.props.colorMixerSettings.colorPreset.label,
        colors: this.props.colorMixerSettings.colorPreset.colors,
        selected: this.mixedColorWithStrippedAlpha ? `#${this.mixedColorWithStrippedAlpha}` : void 0,
        onSelect: color => this.setState({
          mixedColor: color.slice(1)
        })
      })), ((_this$props4 = this.props) === null || _this$props4 === void 0 ? void 0 : (_this$props4$colorMix = _this$props4.colorMixerSettings) === null || _this$props4$colorMix === void 0 ? void 0 : _this$props4$colorMix.colorContrast) && jsx("div", {
        css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.popoverContentBlock
      }, jsx(ColorContrast, {
        firstColor: this.props.colorMixerSettings.colorContrast.firstColor,
        secondColor: `#${this.mixedColorWithStrippedAlpha}`,
        label: this.props.colorMixerSettings.colorContrast.label,
        successLabel: this.props.colorMixerSettings.colorContrast.successLabel,
        failureLabel: this.props.colorMixerSettings.colorContrast.failureLabel,
        normalTextLabel: this.props.colorMixerSettings.colorContrast.normalTextLabel,
        largeTextLabel: this.props.colorMixerSettings.colorContrast.largeTextLabel,
        graphicsTextLabel: this.props.colorMixerSettings.colorContrast.graphicsTextLabel,
        firstColorLabel: this.props.colorMixerSettings.colorContrast.firstColorLabel,
        secondColorLabel: this.props.colorMixerSettings.colorContrast.secondColorLabel
      }))), jsx("div", {
        css: (_this$props$styles5 = this.props.styles) === null || _this$props$styles5 === void 0 ? void 0 : _this$props$styles5.popoverFooter
      }, jsx(Button, {
        onClick: () => this.setState({
          openColorPicker: false,
          mixedColor: this.state.hexCode
        }),
        color: "secondary",
        margin: "xx-small"
      }, (_this$props$colorMixe = this.props.colorMixerSettings) === null || _this$props$colorMixe === void 0 ? void 0 : _this$props$colorMixe.popoverCloseButtonLabel), jsx(Button, {
        onClick: () => {
          if (typeof this.props.onChange === 'function') {
            this.props.onChange(`#${this.mixedColorWithStrippedAlpha}`);
            this.setState({
              openColorPicker: false
            });
          } else {
            this.setState({
              openColorPicker: false,
              hexCode: `${this.mixedColorWithStrippedAlpha}`
            });
          }
        },
        color: "primary",
        margin: "xx-small"
      }, (_this$props$colorMixe2 = this.props.colorMixerSettings) === null || _this$props$colorMixe2 === void 0 ? void 0 : _this$props$colorMixe2.popoverAddButtonLabel)));
    };
    this.state = {
      hexCode: '',
      showHelperErrorMessages: false,
      openColorPicker: false,
      mixedColor: '',
      labelHeight: 0
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props5, _this$props$value;
    (_this$props$makeStyle = (_this$props5 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props5, {
      ...this.state,
      isSimple: this.isSimple
    });
    this.checkSettings();
    this.props.value && this.setState({
      hexCode: (_this$props$value = this.props.value) === null || _this$props$value === void 0 ? void 0 : _this$props$value.slice(1)
    });
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props6;
    (_this$props$makeStyle2 = (_this$props6 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props6, {
      ...this.state,
      isSimple: this.isSimple
    });
    if (prevProps.value !== this.props.value) {
      var _this$props$value2;
      this.setState({
        showHelperErrorMessages: false,
        hexCode: ((_this$props$value2 = this.props.value) === null || _this$props$value2 === void 0 ? void 0 : _this$props$value2.slice(1)) || ''
      });
    }
    this.checkSettings();
  }
  get renderMode() {
    if (this.props.children) {
      return 'customPopover';
    }
    if (this.props.colorMixerSettings) {
      return 'defaultPopover';
    }
    return 'simple';
  }
  get isSimple() {
    return this.renderMode === 'simple';
  }
  get isDefaultPopover() {
    return this.renderMode === 'defaultPopover';
  }
  get isCustomPopover() {
    return this.renderMode === 'customPopover';
  }
  get mixedColorWithStrippedAlpha() {
    const mixedColor = this.state.mixedColor;
    return mixedColor.length === 8 && mixedColor.slice(-2) === 'FF' ? mixedColor.slice(0, -2) : mixedColor;
  }
  getMinContrast(strength) {
    return {
      min: 3,
      mid: 4.5,
      max: 7
    }[strength];
  }
  renderMessages() {
    var _this$props$checkCont;
    const _this$state = this.state,
      hexCode = _this$state.hexCode,
      showHelperErrorMessages = _this$state.showHelperErrorMessages;
    const isValidHex = isValid(hexCode);
    const _this$props7 = this.props,
      checkContrast = _this$props7.checkContrast,
      renderMessages = _this$props7.renderMessages,
      renderInvalidColorMessage = _this$props7.renderInvalidColorMessage,
      renderIsRequiredMessage = _this$props7.renderIsRequiredMessage,
      isRequired = _this$props7.isRequired;
    const contrast = isValidHex ? getContrast(((_this$props$checkCont = this.props.checkContrast) === null || _this$props$checkCont === void 0 ? void 0 : _this$props$checkCont.contrastAgainst) || '#fff', hexCode, 2) : void 0;
    const contrastStrength = checkContrast !== null && checkContrast !== void 0 && checkContrast.contrastStrength ? checkContrast.contrastStrength : 'mid';
    const minContrast = this.getMinContrast(contrastStrength);
    let invalidColorMessages = [];
    let isRequiredMessages = [];
    let generalMessages = [];
    let contrastMessages = [];
    if (checkContrast && contrast) {
      const renderContrastSuccessMessage = checkContrast.renderContrastSuccessMessage,
        renderContrastErrorMessage = checkContrast.renderContrastErrorMessage;
      if (contrast < minContrast) {
        contrastMessages = typeof renderContrastErrorMessage === 'function' ? renderContrastErrorMessage(contrast, minContrast) : checkContrast.isStrict ? [{
          type: 'error',
          text: ''
        }] : [];
      } else if (typeof renderContrastSuccessMessage === 'function') {
        contrastMessages = renderContrastSuccessMessage(contrast, minContrast);
      }
    }
    if (showHelperErrorMessages && hexCode !== '' && !isValidHex && typeof renderInvalidColorMessage === 'function') {
      invalidColorMessages = renderInvalidColorMessage(hexCode);
    }
    if (isRequired && showHelperErrorMessages && hexCode === '') {
      isRequiredMessages = typeof renderIsRequiredMessage === 'function' ? renderIsRequiredMessage() : [{
        type: 'error',
        text: '*'
      }];
    }
    if (typeof renderMessages === 'function') {
      generalMessages = renderMessages(hexCode, isValidHex, minContrast, contrast);
    }
    return [...invalidColorMessages, ...isRequiredMessages, ...generalMessages, ...contrastMessages];
  }
  renderBeforeInput() {
    const styles = this.props.styles;
    return jsx("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.simpleColorContainer
    }, this.isSimple && jsx(ColorIndicator, {
      color: `#${this.state.hexCode}`
    }), jsx("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.hashMarkContainer
    }, "#"));
  }
  renderAfterInput() {
    const _this$props8 = this.props,
      checkContrast = _this$props8.checkContrast,
      styles = _this$props8.styles;
    const hexCode = this.state.hexCode;
    if (checkContrast && isValid(hexCode)) {
      const contrast = getContrast(checkContrast.contrastAgainst || '#fff', hexCode, 2);
      const minContrast = this.getMinContrast(checkContrast.contrastStrength ? checkContrast.contrastStrength : 'mid');
      if (minContrast >= contrast) {
        return jsx("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.errorIcons
        }, checkContrast.isStrict ? _IconTroubleLine || (_IconTroubleLine = jsx(IconTroubleLine, null)) : _IconWarningLine || (_IconWarningLine = jsx(IconWarningLine, null)));
      }
      return jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.successIcon
      }, _IconCheckDarkLine || (_IconCheckDarkLine = jsx(IconCheckDarkLine, null)));
    }
    return null;
  }
  handleOnChange(_event, value) {
    const onChange = this.props.onChange;
    if (value.length > (this.props.withAlpha ? 8 : 6) || value.split('').find(char => !acceptedCharactersForHEX.includes(char)) !== void 0) {
      return;
    }
    if (typeof onChange === 'function') {
      onChange(`#${value}`);
    } else {
      this.setState({
        showHelperErrorMessages: false,
        hexCode: value
      });
    }
  }
  handleOnPaste(event) {
    const pasted = event.clipboardData.getData('Text');
    let toPaste = pasted;
    if (pasted[0] && pasted[0] === '#') {
      toPaste = pasted.slice(1);
    }
    if (this.props.withAlpha && toPaste.length > 8 || !this.props.withAlpha && toPaste.length > 6) {
      return event.preventDefault();
    }
    const newHex = `${this.state.hexCode}${toPaste}`;
    if (isValid(newHex)) {
      if (typeof this.props.onChange === 'function') {
        this.props.onChange(`#${newHex}`);
      } else {
        this.setState({
          showHelperErrorMessages: false,
          hexCode: newHex
        });
      }
    }
    event.preventDefault();
  }
  handleOnBlur() {
    this.setState({
      showHelperErrorMessages: true
    });
  }
  renderLabel() {
    const _this$props9 = this.props,
      label = _this$props9.label,
      tooltip = _this$props9.tooltip,
      styles = _this$props9.styles;
    return tooltip ? jsx("div", null, jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.label
    }, label), jsx("span", null, jsx(Tooltip, {
      renderTip: tooltip
    }, _IconInfoLine || (_IconInfoLine = jsx(IconInfoLine, {
      tabIndex: -1
    }))))) : label;
  }
  render() {
    var _this$props$styles6, _this$props$styles7, _this$props$styles8;
    const _this$props10 = this.props,
      disabled = _this$props10.disabled,
      isRequired = _this$props10.isRequired,
      placeholderText = _this$props10.placeholderText,
      width = _this$props10.width,
      id = _this$props10.id;
    return jsx("div", Object.assign({}, omitProps(this.props, ColorPicker.allowedProps), {
      css: (_this$props$styles6 = this.props.styles) === null || _this$props$styles6 === void 0 ? void 0 : _this$props$styles6.colorPicker,
      ref: this.handleRef
    }), jsx(TextInput, {
      id: id,
      isRequired: isRequired,
      disabled: disabled,
      renderLabel: () => this.renderLabel(),
      display: "inline-block",
      width: width,
      placeholder: placeholderText,
      themeOverride: {
        padding: ''
      },
      renderAfterInput: this.renderAfterInput(),
      renderBeforeInput: this.renderBeforeInput(),
      inputContainerRef: this.handleInputContainerRef,
      value: this.state.hexCode,
      onChange: (event, value) => this.handleOnChange(event, value),
      onPaste: event => this.handleOnPaste(event),
      onBlur: () => this.handleOnBlur(),
      messages: this.renderMessages()
    }), !this.isSimple && jsx("div", {
      css: (_this$props$styles7 = this.props.styles) === null || _this$props$styles7 === void 0 ? void 0 : _this$props$styles7.colorMixerButtonContainer,
      style: {
        paddingTop: this.state.labelHeight
      }
    }, jsx("div", {
      css: (_this$props$styles8 = this.props.styles) === null || _this$props$styles8 === void 0 ? void 0 : _this$props$styles8.colorMixerButtonWrapper
    }, this.renderPopover())));
  }
}, _class2.displayName = "ColorPicker", _class2.propTypes = propTypes, _class2.allowedProps = allowedProps, _class2.componentId = 'ColorPicker', _class2.defaultProps = {
  disabled: false,
  withAlpha: false,
  width: '22.5rem',
  popoverMaxHeight: '100vh'
}, _class2)) || _class) || _class);
export { ColorPicker };
export default ColorPicker;